import React, { useState } from "react";
import { FaPlus, FaEye, FaEdit, FaTrash, FaSearch } from "react-icons/fa";

const ChatbotTable = ({ title, columns, data, onAdd, onView, onEdit, onDelete, rowsPerPage = 5, btnName, handleStatusClick }) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);

  // Filter data dynamically
  const filteredData = data.filter((row) =>
    Object.values(row).some((val) =>
      String(val).toLowerCase().includes(searchTerm.toLowerCase())
    )
  );

  // Pagination logic
  const totalPages = Math.ceil(filteredData.length / rowsPerPage);
  const startIndex = (currentPage - 1) * rowsPerPage;
  const currentData = filteredData.slice(startIndex, startIndex + rowsPerPage);

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  return (
    <div className="bg-white rounded-xl shadow p-6 mb-6 mt-5">
      {/* Header */}
      <div className="flex justify-between items-center mb-4">
        <div className="flex items-center gap-2 mb-4 border rounded-lg px-3 py-2 w-80">
          <FaSearch size={14} className="text-gray-500" />
          <input
            type="text"
            placeholder={`Search ${title.toLowerCase()}...`}
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setCurrentPage(1);
            }}
            className="outline-none w-full text-sm"
          />
        </div>
        <button
          onClick={onAdd}
          className="flex items-center gap-2 px-4 py-2 text-white rounded-lg shadow bg-gradient-to-r from-blue-500 to-blue-700 hover:from-blue-600 hover:to-blue-800 transition"
        >
          <FaPlus size={14} /> {btnName}
        </button>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full border-collapse">
          <thead>
            <tr className="bg-gray-100 text-left">
              {columns.map((col) => (
                <th key={col.key} className="p-3 text-sm font-semibold text-gray-700">
                  {col.label}
                </th>
              ))}
              <th className="p-3 text-sm font-semibold text-gray-700">Actions</th>
            </tr>
          </thead>
          <tbody>
            {currentData.length > 0 ? (
              currentData.map((row) => (
                <tr key={row.id} className="border-b hover:bg-gray-50 transition">
                  {columns.map((col) => (
                    <td
                      key={col.key}
                      className="p-3 text-sm text-gray-800"
                      style={{ width: col.width }}
                    >
                      {col.key === "status" ? (
                        <div
                          onClick={() => handleStatusClick(row)}
                          className={`relative w-12 h-6 flex items-center rounded-full p-1 cursor-pointer transition-colors
          ${row[col.key] === 1 ? "bg-green-500" : "bg-red-500"}`}
                        >
                          <div
                            className={`bg-white w-4 h-4 rounded-full shadow-md transform transition-transform
            ${row[col.key] === 1 ? "translate-x-6" : "translate-x-0"}`}
                          ></div>
                        </div>
                      ) : (
                        row[col.key]
                      )}
                    </td>
                  ))}
                  <td className="p-3 flex gap-3">
                    <button onClick={() => onView(row)} className="text-blue-500 hover:text-blue-700">
                      <FaEye size={14} />
                    </button>
                    <button onClick={() => onEdit(row)} className="text-green-500 hover:text-green-700">
                      <FaEdit size={14} />
                    </button>
                    <button onClick={() => onDelete(row)} className="text-red-500 hover:text-red-700">
                      <FaTrash size={14} />
                    </button>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={columns.length + 1} className="p-3 text-center text-gray-500">
                  No {title.toLowerCase()} found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-between items-center mt-4 text-sm">
          <span>
            Showing {startIndex + 1} - {Math.min(startIndex + rowsPerPage, filteredData.length)} of {filteredData.length}
          </span>
          <div className="flex gap-2">
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className={`px-3 py-1 rounded-lg ${currentPage === 1
                ? "bg-gray-200 text-gray-500 cursor-not-allowed"
                : "bg-blue-600 text-white hover:bg-blue-700"
                }`}
            >
              Prev
            </button>

            {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
              <button
                key={page}
                onClick={() => handlePageChange(page)}
                className={`px-3 py-1 rounded-lg ${currentPage === page
                  ? "bg-blue-600 text-white"
                  : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                  }`}
              >
                {page}
              </button>
            ))}

            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className={`px-3 py-1 rounded-lg ${currentPage === totalPages
                ? "bg-gray-200 text-gray-500 cursor-not-allowed"
                : "bg-blue-600 text-white hover:bg-blue-700"
                }`}
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChatbotTable;
